import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { SketchPicker } from "react-color";

const AddCategory = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    catTitle: "",
    description: "",
    taxonomy: "video",
    catid: "",
    catchild: "",
    grandchild: "",
    isLeaf: false,
    premium: false,
  });

  const [terms, setTerms] = useState([]);
  const [children, setChildren] = useState([]);
  const [grandChildren, setGrandChildren] = useState([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLeafLocked, setIsLeafLocked] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  useEffect(() => {
    fetchAllCategories();
  }, []);

  useEffect(() => {
    if (formData.catid) {
      const parent = terms.find((cat) => cat.id === parseInt(formData.catid));
      setChildren(parent?.children || []);
      setFormData((prev) => ({
        ...prev,
        catchild: "",
        grandchild: "",
      }));
      setGrandChildren([]);
    } else {
      setChildren([]);
      setFormData((prev) => ({
        ...prev,
        catchild: "",
        grandchild: "",
      }));
      setGrandChildren([]);
    }
  }, [formData.catid, terms]);

  useEffect(() => {
    if (formData.grandchild) {
      setIsLeafLocked(true);
      setFormData((prev) => ({
        ...prev,
        isLeaf: true, // Auto-check when grandchild is selected
      }));
    } else {
      setIsLeafLocked(false);
    }
  }, [formData.grandchild]);

  useEffect(() => {
    if (formData.catchild) {
      const child = children.find(
        (cat) => cat.id === parseInt(formData.catchild)
      );
      setGrandChildren(child?.children || []);
      setFormData((prev) => ({
        ...prev,
        grandchild: "",
      }));
    } else {
      setGrandChildren([]);
      setFormData((prev) => ({
        ...prev,
        grandchild: "",
      }));
    }
  }, [formData.catchild, children]);

  const fetchAllCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/fetch-categories`,
        {
          params: {
            taxonomy: "",
          },
        }
      );
      setTerms(response.data.data.terms); // Updated to match your API response structure
    } catch (error) {
      setError("Error fetching terms: " + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    if (!formData.catTitle.trim()) {
      setError("Please enter the Category name");
      setIsSubmitting(false);
      return;
    }

    const taxonomy = formData.isLeaf ? formData.taxonomy : null;
    const parentId = formData.grandchild
      ? formData.grandchild
      : formData.catchild
      ? formData.catchild
      : formData.catid
      ? formData.catid
      : null;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/add-category`,
        {
          name: formData.catTitle,
          description: formData.description,
          taxonomy,
          parent_id: parentId,
          premium: formData.premium,
          color: formData.color,
        }
      );

      if (response.data.success) {
        navigate("/xl-admin/categories?action=added");
      } else {
        throw new Error(response.data.message || "Failed to add category");
      }
    } catch (error) {
      setError(
        error.response?.data?.message || error.message || "An error occurred"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h3 className="mb-4">Add a Category</h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Category Name */}
          <div className="col-md-6 mb-3">
            <label htmlFor="catTitle" className="form-label">
              Category Name
            </label>
            <input
              type="text"
              id="catTitle"
              name="catTitle"
              className="form-control"
              value={formData.catTitle}
              onChange={handleChange}
              required
            />
          </div>

          {/* Parent Category */}
          <div className="col-md-6 mb-3">
            <label htmlFor="catid" className="form-label">
              Parent Category
            </label>
            <select
              id="catid"
              name="catid"
              className="form-select"
              value={formData.catid}
              onChange={handleChange}
            >
              <option value="">-- Select Parent --</option>
              {terms.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Child Category */}
          {children.length > 0 && (
            <div className="col-md-6 mb-3">
              <label htmlFor="catchild" className="form-label">
                Child Category
              </label>
              <select
                id="catchild"
                name="catchild"
                className="form-select"
                value={formData.catchild}
                onChange={handleChange}
              >
                <option value="">-- Select Child --</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Grandchild Category */}
          {grandChildren.length > 0 && (
            <div className="col-md-6 mb-3">
              <label htmlFor="grandchild" className="form-label">
                Grandchild Category
              </label>
              <select
                id="grandchild"
                name="grandchild"
                className="form-select"
                value={formData.grandchild}
                onChange={handleChange}
              >
                <option value="">-- Select Grandchild --</option>
                {grandChildren.map((gc) => (
                  <option key={gc.id} value={gc.id}>
                    {gc.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Color Picker */}
          <div className="col-md-6 mb-3 position-relative">
            <label className="form-label d-block">Category Color</label>

            {/* Swatch */}
            <div
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{
                width: "50px",
                height: "40px",
                background: formData.color,
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Click to pick color"
            />

            {/* SketchPicker */}
            {showColorPicker && (
              <div
                style={{ position: "absolute", zIndex: 10, marginTop: "10px" }}
              >
                <SketchPicker
                  color={formData.color}
                  onChange={(color) =>
                    setFormData((prev) => ({ ...prev, color: color.hex }))
                  }
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="col-md-12 mb-3">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              className="form-control"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Is Leaf Checkbox */}
          <div className="mb-3 form-check">
            <label htmlFor="isLeaf" className="form-check-label">
              Is this a leaf? (End category that can contain content)
            </label>
            <input
              type="checkbox"
              id="isLeaf"
              name="isLeaf"
              className="form-check-input"
              checked={formData.isLeaf}
              onChange={handleChange}
              disabled={isLeafLocked} // Disable when locked
            />
          </div>

          {/* Type Selector */}
          {formData.isLeaf && (
            <div className="col-md-6 mb-3">
              <label htmlFor="taxonomy" className="form-label">
                Content Type
              </label>
              <select
                id="taxonomy"
                name="taxonomy"
                className="form-select"
                value={formData.taxonomy}
                onChange={handleChange}
              >
                <option value="video">Video</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>
          )}

          {/* Premium Checkbox */}
          {formData.isLeaf && (
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                id="premium"
                name="premium"
                className="form-check-input"
                checked={formData.premium}
                onChange={handleChange}
              />
              <label htmlFor="premium" className="form-check-label">
                Premium
              </label>
            </div>
          )}
        </div>

        <div className="text-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn btn-primary ${isSubmitting ? "disabled" : ""}`}
          >
            {isSubmitting ? "Adding..." : "Add Category"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCategory;
